import type {Slide} from '../core/model';
import {
  presentationRegistry,
  type BackgroundController,
  type PresentationRegistry,
} from '../core/presentation-registry';
import {AnimationHost} from './animation-host';
import {renderSlideBody} from './markdown';
import {VideoFocus} from './video-focus';
import {autoplayEmbedUrl} from './video';

type StageOptions = {
  deckId: string;
  resolveAsset?: (source: string) => string;
  registry?: PresentationRegistry;
  focusVideos?: boolean;
};

export type StageRenderContext = {
  language?: string;
  chains?: Record<string, string[]>;
  theme?: string;
  style?: Record<string, string>;
};

export class SlideStage {
  readonly element: HTMLElement;
  readonly stage: HTMLElement;
  #options: StageOptions;
  #registry: PresentationRegistry;
  #resizeObserver: ResizeObserver;
  #animations: AnimationHost[] = [];
  #background?: BackgroundController;
  #theme?: string;
  #tokenProperties = new Set<string>();
  #layoutProperties = new Set<string>();
  #renderedSlide?: Slide;
  #suspended = false;
  #videoFocus?: VideoFocus;
  #handleVisibility = () => {
    if (document.hidden) this.suspendMedia(this.stage);
    else this.restoreVisibleMedia();
  };
  #handlePageHide = () => this.suspendMedia(this.stage);

  constructor(container: HTMLElement, options: StageOptions) {
    this.#options = options;
    this.#registry = options.registry ?? presentationRegistry;
    this.element = document.createElement('div');
    this.element.className = 'stage-viewport';
    this.stage = document.createElement('article');
    this.stage.className = 'slide-stage';
    this.element.append(this.stage);
    if (options.focusVideos) this.#videoFocus = new VideoFocus(this.stage, this.element);
    container.replaceChildren(this.element);
    this.#resizeObserver = new ResizeObserver(() => this.resize());
    this.#resizeObserver.observe(this.element);
    document.addEventListener('visibilitychange', this.#handleVisibility);
    window.addEventListener('pagehide', this.#handlePageHide);
  }

  render(slide: Slide, step = 0, context: StageRenderContext = {}): void {
    if (this.#renderedSlide === slide) {
      this.syncReveal(step);
      return;
    }
    this.suspendMedia(this.stage);
    this.#videoFocus?.clear();
    this.destroyAnimations();
    this.#renderedSlide = slide;
    const theme = this.#registry.resolveThemeId(context.theme);
    const requestedLayout = String(slide.meta.layout ?? 'content');
    const layout = this.#registry.resolveLayout(theme, requestedLayout);
    const background = String(
      slide.meta.background ?? this.#registry.defaultBackground(theme),
    );
    const align = String(slide.meta.align ?? '');
    if (theme !== this.#theme) {
      this.#background?.destroy();
      this.#background = this.#registry.createBackground(theme);
      this.#theme = theme;
    }
    this.stage.className = 'slide-stage';
    this.stage.classList.toggle('has-multiple-media', slide.media.length > 1);
    this.stage.dataset.mediaCount = String(slide.media.length);
    this.stage.dataset.theme = theme;
    this.stage.lang = String(slide.meta.lang ?? context.language ?? 'en');
    this.stage.dataset.background = background;
    this.stage.dataset.layout = layout.id;
    this.stage.dataset.layoutBase = layout.base ?? layout.id;
    delete this.stage.dataset.layoutRequested;
    if (layout.requested !== layout.id) {
      this.stage.dataset.layoutRequested = layout.requested;
    }
    this.stage.dataset.header = slide.meta.header === false ? 'hidden' : 'visible';
    this.applyTokens(theme, context.style, slide.meta.style);
    this.applyLayoutVariables(layout, slide.meta.layoutOptions);
    const mediaOptions =
      slide.meta.media && typeof slide.meta.media === 'object'
        ? (slide.meta.media as Record<string, unknown>)
        : {};
    delete this.stage.dataset.gap;
    delete this.stage.dataset.mediaFit;
    delete this.stage.dataset.align;
    if (mediaOptions.gap) this.stage.dataset.gap = String(mediaOptions.gap);
    if (mediaOptions.fit) this.stage.dataset.mediaFit = String(mediaOptions.fit);
    if (align) this.stage.dataset.align = align;

    const frame = document.createElement('div');
    frame.className = 'slide-frame';
    const header = document.createElement('header');
    header.className = 'slide-header';
    const section = document.createElement('div');
    section.className = 'slide-section';
    section.textContent = String(slide.meta.section ?? '');
    const {root, animationTargets} = renderSlideBody(slide, step, {
      resolveAsset: (source) => this.resolveAsset(source),
      structure: layout.structure,
    });
    const title = root.querySelector(':scope > h1');
    header.append(section);
    if (title) {
      header.append(title);
      if (
        title instanceof HTMLElement &&
        layout.fitTitle
      ) {
        const characters = Math.max(1, title.textContent?.trim().length ?? 1);
        title.style.fontSize = `${Math.max(54, Math.min(110, 1360 / (characters * 0.56)))}px`;
      }
    }
    if (slide.meta.header !== false) frame.append(header);
    frame.append(root);
    const chain = this.createChain(
      String(slide.meta.chain ?? ''),
      context.chains ?? {},
    );
    if (chain) frame.append(chain);
    this.stage.replaceChildren(frame);
    void this.#background?.update(this.stage, background);

    for (const target of animationTargets) {
      const host = new AnimationHost(theme, this.#registry);
      this.#animations.push(host);
      void host.mount(target, target.dataset.component ?? '');
    }
    this.syncReveal(step);
    this.resize();
  }

  pause(): void {
    for (const animation of this.#animations) animation.pause();
  }

  play(): void {
    for (const animation of this.#animations) animation.play();
  }

  reset(): void {
    for (const animation of this.#animations) animation.reset();
  }

  /** Asset URLs can change without the parsed slide changing. */
  invalidate(): void {
    this.#renderedSlide = undefined;
  }

  clear(): void {
    this.suspendMedia(this.stage);
    this.#videoFocus?.clear();
    this.destroyAnimations();
    this.#renderedSlide = undefined;
    this.#background?.destroy();
    this.#background = undefined;
    this.#theme = undefined;
    this.stage.replaceChildren();
  }

  setMediaSuspended(suspended: boolean): void {
    this.#suspended = suspended;
    if (suspended) this.suspendMedia(this.stage);
    else this.restoreVisibleMedia();
  }

  destroy(): void {
    this.clear();
    this.#resizeObserver.disconnect();
    document.removeEventListener('visibilitychange', this.#handleVisibility);
    window.removeEventListener('pagehide', this.#handlePageHide);
    this.element.remove();
  }

  private resolveAsset(source: string): string {
    if (this.#options.resolveAsset) return this.#options.resolveAsset(source);
    if (/^(blob:|data:|https?:)/.test(source)) return source;
    if (source.startsWith('assets/')) {
      return `/decks/${encodeURIComponent(this.#options.deckId)}/${source}`;
    }
    return source;
  }

  private applyTokens(
    theme: string,
    deckStyle: Record<string, string> = {},
    rawSlideStyle: unknown,
  ): void {
    for (const property of this.#tokenProperties) this.stage.style.removeProperty(property);
    this.#tokenProperties.clear();
    const tokens = this.#registry.resolveTokens(theme);
    const slideStyle =
      rawSlideStyle && typeof rawSlideStyle === 'object' && !Array.isArray(rawSlideStyle)
        ? Object.fromEntries(
            Object.entries(rawSlideStyle).flatMap(([key, value]) =>
              typeof value === 'string' ? [[key, value]] : [],
            ),
          )
        : {};
    for (const [key, fallback] of Object.entries(tokens)) {
      const property = `--slide-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
      this.stage.style.setProperty(property, slideStyle[key] ?? deckStyle[key] ?? fallback);
      this.#tokenProperties.add(property);
    }
  }

  private applyLayoutVariables(
    layout: {variables?: Record<string, string>},
    rawOptions: unknown,
  ): void {
    for (const property of this.#layoutProperties) this.stage.style.removeProperty(property);
    this.#layoutProperties.clear();
    const options =
      rawOptions && typeof rawOptions === 'object' && !Array.isArray(rawOptions)
        ? (rawOptions as Record<string, unknown>)
        : {};
    for (const [key, fallback] of Object.entries(layout.variables ?? {})) {
      const property = `--layout-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
      const override = options[key];
      this.stage.style.setProperty(
        property,
        typeof override === 'string' ? override : fallback,
      );
      this.#layoutProperties.add(property);
    }
  }

  private createChain(
    reference: string,
    chains: Record<string, string[]>,
  ): HTMLElement | undefined {
    const separator = reference.indexOf(':');
    if (separator < 1) return undefined;
    const nodes = chains[reference.slice(0, separator)];
    const active = reference.slice(separator + 1);
    const activeIndex = nodes?.indexOf(active) ?? -1;
    if (!nodes || activeIndex < 0) return undefined;

    const navigation = document.createElement('nav');
    navigation.className = 'slide-chain';
    navigation.setAttribute('aria-label', 'Position i öppningskedjan');
    for (const [index, node] of nodes.entries()) {
      if (index > 0) {
        const arrow = document.createElement('span');
        arrow.className = 'slide-chain-arrow';
        arrow.textContent = '→';
        navigation.append(arrow);
      }
      const step = document.createElement('span');
      step.className = 'slide-chain-node';
      step.classList.toggle('is-past', index < activeIndex);
      step.classList.toggle('is-active', index === activeIndex);
      step.textContent = node;
      navigation.append(step);
    }
    return navigation;
  }

  private resize(): void {
    const width = this.element.clientWidth;
    const height = this.element.clientHeight;
    const scale = Math.min(width / 1600, height / 900);
    this.stage.style.transform = `translate(-50%, -50%) scale(${scale})`;
  }

  private destroyAnimations(): void {
    for (const animation of this.#animations) animation.destroy();
    this.#animations = [];
  }

  private syncReveal(step: number): void {
    for (const element of this.stage.querySelectorAll<HTMLElement>('[data-reveal-step]')) {
      const hidden = Number(element.dataset.revealStep) > step;
      element.classList.toggle('is-hidden', hidden);
    }
    this.#videoFocus?.sync(this.#renderedSlide?.title ?? '');
    for (const figure of this.stage.querySelectorAll<HTMLElement>('.slide-video')) {
      if (figure.closest('.is-hidden') || this.#suspended || document.hidden ||
        this.#videoFocus && !this.#videoFocus.isActive(figure)) this.suspendMedia(figure);
      else this.restoreMedia(figure);
    }
  }

  private suspendMedia(root: ParentNode): void {
    for (const media of root.querySelectorAll<HTMLIFrameElement | HTMLVideoElement>(
      '.slide-video iframe, .slide-video video',
    )) {
      const source = media.getAttribute('src');
      if (!source || source === 'about:blank') continue;
      media.dataset.playbackSrc = source;
      if (media instanceof HTMLVideoElement) {
        media.pause();
        media.removeAttribute('src');
        media.load();
      } else {
        media.src = 'about:blank';
      }
    }
  }

  private restoreMedia(root: ParentNode): void {
    for (const media of root.querySelectorAll<HTMLIFrameElement | HTMLVideoElement>(
      '.slide-video iframe[data-playback-src], .slide-video video[data-playback-src]',
    )) {
      const source = media.dataset.playbackSrc;
      if (!source) continue;
      delete media.dataset.playbackSrc;
      const figure = media.closest('.slide-video');
      const autoplay = !!figure && !!this.#videoFocus?.isActive(figure);
      if (media instanceof HTMLVideoElement) {
        media.autoplay = autoplay;
        const start = Math.max(0, Number(media.dataset.playbackStart) || 0);
        if (start > 0) {
          media.addEventListener(
            'loadedmetadata',
            () => {
              media.currentTime = Math.min(start, Number.isFinite(media.duration) ? media.duration : start);
            },
            {once: true},
          );
        }
      }
      media.src = autoplay && media instanceof HTMLIFrameElement ? autoplayEmbedUrl(source) : source;
      if (media instanceof HTMLVideoElement) {
        media.load();
        if (autoplay) void media.play().catch(error => {
          // A fresh browser may require Play once. Keep native controls usable;
          // never mute a training film or claim that rejected playback started.
          if (error.name !== 'AbortError') media.dataset.playbackError = error.name;
        });
      }
    }
  }

  private restoreVisibleMedia(): void {
    if (this.#suspended || document.hidden) return;
    for (const figure of this.stage.querySelectorAll<HTMLElement>('.slide-video')) {
      if (!figure.closest('.is-hidden') && (!this.#videoFocus || this.#videoFocus.isActive(figure))) this.restoreMedia(figure);
    }
  }
}
